import { __decorate, __rest } from 'tslib';
import { ɵɵdefineInjectable, Injectable, EventEmitter, Output, HostListener, Directive, Input, ContentChild, Component, NgModule, ɵɵinject } from '@angular/core';
import { Subject, BehaviorSubject, Observable, timer, of } from 'rxjs';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { map, tap, catchError, finalize, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { HttpClient } from '@angular/common/http';
import urlJoin from 'proper-url-join';
import { select, createSelector, createFeatureSelector, Store } from '@ngrx/store';
import { UtilsModule } from '@lbmx/phoenix-lib-utils';
import { AccordionModule, ButtonModule, CheckboxModule as CheckboxModule$1, MultiSelectModule as MultiSelectModule$1, RadioButtonModule, TreeModule, CalendarModule as CalendarModule$1, InputNumberModule, ConfirmDialogModule as ConfirmDialogModule$1, DropdownModule, TabViewModule, ToolbarModule, TreeTableModule, ProgressSpinnerModule, DialogModule as DialogModule$1, TableModule as TableModule$1, ConfirmationService as ConfirmationService$1 } from 'primeng-lts';
import { Action, ActionUiService } from '@lbmx/action-ui';
import add$1 from 'date-fns/add';
import { add } from 'date-fns';

let SearchContainerService = class SearchContainerService {
    constructor() {
        this.lazyLoadSubject = new Subject();
        this.fieldEventSubject = new Subject();
        this._selectedIndices = [];
        this.lazyLoad$ = this.lazyLoadSubject.asObservable();
        this.fieldEvent$ = this.fieldEventSubject.asObservable();
        this.hasWarningsColumn = false;
        this.data$ = new BehaviorSubject([]);
    }
    set selection(selected) {
        var _a, _b, _c, _d;
        this._selectedIndices = (_d = (_c = (_b = (_a = this.data$.value) === null || _a === void 0 ? void 0 : _a.map((row, idx) => ({ data: row, index: idx }))) === null || _b === void 0 ? void 0 : _b.filter(({ data }) => selected.includes(data))) === null || _c === void 0 ? void 0 : _c.map(({ index }) => index)) !== null && _d !== void 0 ? _d : [];
    }
    get selection() {
        var _a;
        return (_a = this._selectedIndices) === null || _a === void 0 ? void 0 : _a.map((idx) => this.data$.value[idx]);
    }
    onLazyLoad($event) {
        this.lazyLoadSubject.next($event);
    }
    onFieldEvent($event) {
        this.fieldEventSubject.next($event);
    }
};
SearchContainerService.ɵprov = ɵɵdefineInjectable({ factory: function SearchContainerService_Factory() { return new SearchContainerService(); }, token: SearchContainerService, providedIn: "root" });
SearchContainerService = __decorate([
    Injectable({
        providedIn: 'root',
    })
], SearchContainerService);

/**
 * Scenario 1. A name in the columnsToDisplay list does not exist in the list of columns
 *  This is a developer error and will not be handled here.
 * Scenario 2. A name in the columnOrder is not in the list of columnsToDisplay
 *  The columnsToDisplay should be overridden by columnOrder in the gridState.
 * Scenario 3. A name in the columnOrder is not in the list of columns,
 * for example if the name of a column has changed
 *  The old column name needs to be removed from columnOrder
 *  The old column name needs to be removed from sortField
 *  If the sortField contained the old name remove sortField and sortOrder properties
 */
/**
 * Scenario 1. Column name is changed
 *   a) If the old column name is in the columnOrder property, remove it
 *   b) If the old column name is in the sortField property, remove sortField and sortOrder properties
 */
let LocalStorageService = class LocalStorageService {
    constructor() {
        this.defaultState = {
            first: 0,
            rows: 25,
            selection: [],
        };
    }
    getSelection(stateKey) {
        const storage = this.getStateFromLocalStorage(stateKey);
        return storage.selection || [];
    }
    getProperty(stateKey, property) {
        const storage = this.getStateFromLocalStorage(stateKey);
        return storage[property];
    }
    getStateFromLocalStorage(stateKey) {
        return JSON.parse(localStorage.getItem(stateKey));
    }
    removeColumnWidth(stateKey, columnName) {
        const gridState = this.getStateFromLocalStorage(stateKey);
        const columnIndices = [
            0,
            ...gridState.columnOrder.reduce((indices, c, i) => (c === columnName ? indices : [...indices, i + 1]), []),
        ];
        const columnWidths = this.getColumnWidths(columnIndices, gridState.columnWidths);
        this.mergeWithLocalGridState(stateKey, { columnWidths });
    }
    removeColumnWidths(stateKey, columnNames) {
        const gridState = this.getStateFromLocalStorage(stateKey);
        const columnIndices = [
            0,
            ...gridState.columnOrder.reduce((indices, c, i) => columnNames.includes(c) ? indices : [...indices, i + 1], []),
        ];
        const columnWidths = this.getColumnWidths(columnIndices, gridState.columnWidths);
        this.mergeWithLocalGridState(stateKey, { columnWidths });
    }
    addColumnWidth(stateKey, columnWidth) {
        const gridState = this.getStateFromLocalStorage(stateKey);
        const columnWidthsArray = [
            ...gridState.columnWidths.split(','),
            columnWidth,
        ];
        this.mergeWithLocalGridState(stateKey, {
            columnWidths: columnWidthsArray.join(','),
        });
    }
    initLocalGridState(stateKey, availableColumns, overrides = {}) {
        const { columnOrder, columnWidths } = overrides;
        const gridState = this.getStateFromLocalStorage(stateKey);
        const migratedState = gridState
            ? this.coalesce(columnOrder, availableColumns, gridState)
            : this.coalesce(columnOrder, availableColumns, Object.assign(Object.assign({}, this.defaultState), overrides));
        const state = Object.assign(Object.assign({}, this.defaultState), migratedState);
        localStorage.setItem(stateKey, JSON.stringify(state));
    }
    setLocalGridState(stateKey, overrides = {}) {
        const state = Object.assign(Object.assign({}, this.defaultState), overrides);
        localStorage.setItem(stateKey, JSON.stringify(state));
    }
    mergeWithLocalGridState(stateKey, overrides = {}) {
        const state = Object.assign(Object.assign({}, this.getStateFromLocalStorage(stateKey)), overrides);
        localStorage.setItem(stateKey, JSON.stringify(state));
    }
    stateExists(stateKey) {
        return localStorage.getItem(stateKey) !== null;
    }
    coalesce(columnOrder, columns, gridState) {
        const validColumns = this.getValidColumns(gridState.columnOrder || columnOrder, columns.map((c) => c.field));
        const validGridState = this.cleanState(gridState, validColumns);
        return validGridState;
    }
    getValidColumns(columnOrder, columns) {
        return columnOrder.reduce((validColumns, column, i) => {
            return columns.includes(column)
                ? [...validColumns, { name: column, index: i }]
                : validColumns;
        }, []);
    }
    getColumnWidths(columnIndices, columnWidths) {
        const columnWidthArray = columnWidths.split(',');
        return columnWidthArray
            .filter((_, i) => columnIndices.includes(i))
            .join(',');
    }
    cleanState(gridState, validColumns) {
        const _a = Object.assign({}, gridState), { sortField, sortOrder } = _a, rest = __rest(_a, ["sortField", "sortOrder"]);
        const validColumnNames = validColumns.map((c) => c.name);
        const validColumnIndices = [0, ...validColumns.map((c) => c.index + 1)];
        const columnWidthArray = gridState.columnWidths.split(',');
        return Object.assign({}, Object.assign({}, rest), { columnOrder: validColumnNames }, {
            columnWidths: this.getColumnWidths(validColumnIndices, gridState.columnWidths),
        }, validColumnNames.includes(sortField) ? { sortField, sortOrder } : {});
    }
};
LocalStorageService.ɵprov = ɵɵdefineInjectable({ factory: function LocalStorageService_Factory() { return new LocalStorageService(); }, token: LocalStorageService, providedIn: "root" });
LocalStorageService = __decorate([
    Injectable({
        providedIn: 'root',
    })
], LocalStorageService);

let SearchDirective = class SearchDirective {
    constructor() {
        this.searchEvent = new EventEmitter();
    }
    search($event) {
        this.searchEvent.emit($event);
    }
};
__decorate([
    Output()
], SearchDirective.prototype, "searchEvent", void 0);
__decorate([
    HostListener('setFilterEvent', ['$event'])
], SearchDirective.prototype, "search", null);
SearchDirective = __decorate([
    Directive({
        selector: '[libSearch]',
    })
], SearchDirective);

let SearchContainerComponent = class SearchContainerComponent {
    constructor(formBuilder, router, localStorage, searchContainerService, translocoService) {
        this.formBuilder = formBuilder;
        this.router = router;
        this.localStorage = localStorage;
        this.searchContainerService = searchContainerService;
        this.translocoService = translocoService;
        this.disableAdvancedSearch = false;
        this.lazy = false;
        this.lazyLoadOnInit = true;
        this.loading = false;
        this.resetSelectionOnDestroy = false;
        this.clearSimpleSearch = new Observable();
        this.clear = new EventEmitter();
        this.lazyLoad = new EventEmitter();
        this.paged = new EventEmitter();
        this.query = new EventEmitter();
        this.toolbar = new EventEmitter();
        this.field = new EventEmitter();
        this.singleCheckBoxSelectEvent = new EventEmitter();
        this.searchFilterUpdated = new EventEmitter();
        this.columnMap = new Map();
        this.subscriptions = [];
        this.disabledMode = [false, true, true, true];
        this.selectColumns = false;
        this.showAdvancedSearch = false;
        this.defaultColumnWidth = 250;
        this.columnMenuItems = {
            more: [
                {
                    label: 'Select Columns',
                    command: () => this.toggleColumnDialog(),
                },
                {
                    label: 'Reset Columns',
                    command: () => this.onResetClick(),
                },
            ],
        };
        this.searchContainerService.lazyLoad$.subscribe(($event) => this.lazyLoad.emit(Object.assign(Object.assign({}, $event), { filters: this.searchFilter })));
        this.searchContainerService.fieldEvent$.subscribe(($event) => {
            this.field.emit($event);
        });
    }
    set buttonsSetter(buttons) {
        this.buttons = buttons.filter((button) => !button.parent);
        this.buttons = this.buttons.map((button) => {
            if (button.items !== undefined) {
                button.items = button.items.map((c) => ({
                    label: c.label,
                    disabled: c.disabled,
                    command: () => this.onToolbarClick(c.name),
                }));
            }
            return button;
        });
        this.childButtons = buttons.filter((button) => button.parent).reverse();
        this.translateLabels();
        this.configureButtons();
    }
    set columnsSetter(columns) {
        this.columns = columns.sort((c1, c2) => {
            if (c1.header > c2.header) {
                return 1;
            }
            if (c1.header < c2.header) {
                return -1;
            }
            return 0;
        });
        this.selectedColumnsOptions = columns.map((column) => ({
            label: column.header,
            value: column,
            disabled: !!column.required,
        }));
    }
    set columnsToDisplaySetter(columnsToDisplay) {
        this.defaultColumnOrder = columnsToDisplay;
    }
    set resultsSetter(results) {
        this.handleResultsInputChange(results);
    }
    ngOnDestroy() {
        if (this.resetSelectionOnDestroy) {
            this.localStorage.mergeWithLocalGridState(this.gridStateKey, {
                selection: [],
            });
        }
    }
    ngOnInit() {
        var _a, _b, _c, _d;
        this.columns.forEach((column, index) => this.columnMap.set(column.field, index));
        this.gridStateKey = this.routerGridStateKey();
        if (!this.localStorage.stateExists(this.gridStateKey)) {
            this.setDefaultColumWidths();
            // Initialize the grid state
            this.localStorage.initLocalGridState(this.gridStateKey, this.columns, {
                columnOrder: this.defaultColumnOrder,
                columnWidths: this.columnWidths,
                sortField: (_a = this.paging) === null || _a === void 0 ? void 0 : _a.sortField,
                sortOrder: (_b = this.paging) === null || _b === void 0 ? void 0 : _b.sortOrder,
            });
            this.translateLabels();
        }
        // set default sortField and sortOrder
        this.defaultSortField = (_c = this.paging) === null || _c === void 0 ? void 0 : _c.sortField;
        this.defaultSortOrder = (_d = this.paging) === null || _d === void 0 ? void 0 : _d.sortOrder;
        // Column Configuration
        this.columnOrder = this.localStorage.getProperty(this.gridStateKey, 'columnOrder');
        this.columnWidths = this.localStorage.getProperty(this.gridStateKey, 'columnWidths');
        // Initialize paging
        const defaultPageSizes = this.paging && this.paging.sizes ? this.paging.sizes : [25, 50, 100];
        this.defaultPaging = Object.assign({
            sizes: defaultPageSizes,
            page: { first: 0, rows: defaultPageSizes[0] },
        });
        this.paging = Object.assign(Object.assign({}, this.defaultPaging), this.paging);
        this.selectedColumns = this.columns.filter((c) => this.localStorage
            .getProperty(this.gridStateKey, 'columnOrder')
            .includes(c.field));
        // Forms
        this.selectedColumnsForm = this.formBuilder.group({
            selected: [this.selectedColumns],
        });
        this.selectedColumnsForm.valueChanges.subscribe((selection) => this.handleColumnSelect(selection));
        this.searchForm = this.formBuilder.group({
            [this.searchProperty]: '',
        });
        this.searchFilter = {};
        this.items = this.getMenuItems();
        this.showGrid = true;
        if (this.lazyLoadOnInit && this.initialQuery) {
            this.searchFilter = this.initialQuery;
            timer(0).subscribe(() => {
                this.handleSetSearchFilters({
                    filters: this.searchFilter,
                    options: { search: true },
                });
            });
        }
        this.subscriptions.push(this.clearSimpleSearch.subscribe(() => this.onClearClick()));
    }
    setDefaultColumWidths() {
        this.columnWidths = [
            48,
            ...Array(this.defaultColumnOrder.length).fill(this.defaultColumnWidth),
        ].toString();
    }
    searchOnEnter($event) {
        if ($event.key === 'Enter') {
            this.onSearchClick();
        }
    }
    ngAfterContentInit() {
        if (this.advSearch !== undefined) {
            this.advSearch.searchEvent.subscribe(($event) => {
                this.handleSetSearchFilters($event);
            });
        }
    }
    handlePageEvent($event) {
        this.paged.emit($event);
    }
    onClearClick() {
        const _a = this.searchFilter, _b = this.searchProperty, _ = _a[_b], filters = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
        this.searchForm.reset({ [this.searchProperty]: '' });
        this.handleSetSearchFilters({ filters, options: { clearResults: true } });
    }
    toggleColumnDialog() {
        this.selectColumns = !this.selectColumns;
    }
    onResetClick() {
        this.columnOrder = this.defaultColumnOrder;
        this.setDefaultColumWidths();
        this.setDefaultGridState();
        this.selectedColumnsForm.reset({ selected: this.selectedColumns });
        this.handleSearch();
    }
    setDefaultGridState() {
        this.localStorage.setLocalGridState(this.gridStateKey, {
            columnOrder: this.columnOrder,
            columnWidths: this.columnWidths,
            sortField: this.defaultSortField,
            sortOrder: this.defaultSortOrder,
        });
        this.selectedColumns = this.columns.filter((c) => this.localStorage
            .getProperty(this.gridStateKey, 'columnOrder')
            .includes(c.field));
    }
    updateSelectedColumns() {
        const currentState = this.localStorage.getStateFromLocalStorage(this.gridStateKey);
        this.localStorage.setLocalGridState(this.gridStateKey, Object.assign(Object.assign({}, currentState), { columnOrder: this.columnOrder, columnWidths: this.columnWidths }));
        this.selectedColumns = this.columns.filter((c) => this.localStorage
            .getProperty(this.gridStateKey, 'columnOrder')
            .includes(c.field));
    }
    onSearchClick() {
        const { [this.searchProperty]: searchText } = this.searchForm.value;
        const searchTerm = searchText === '' ? {} : { [this.searchProperty]: searchText };
        const filters = Object.assign(Object.assign({}, this.searchFilter), searchTerm);
        this.handleSetSearchFilters({ filters, options: { search: true } });
    }
    onToolbarClick(command) {
        let selection = [];
        switch (command) {
            case 'new':
                break;
            default:
                selection = this.localStorage.getSelection(this.gridStateKey);
                break;
        }
        this.toolbar.emit({
            command,
            selection,
            clearSelection: () => this.cleanup(),
        });
    }
    handleSearch() {
        /**
         * TODO: refactor this call since handleSetSearchFilters
         * will call this method if the search option is true
         */
        this.handleSetSearchFilters({ filters: this.searchFilter });
        const { first, rows, sortField, sortOrder } = this.localStorage.getStateFromLocalStorage(this.gridStateKey);
        this.query.emit({
            filters: this.searchFilter,
            first,
            rows,
            sortField,
            sortOrder,
        });
    }
    handleSelection() {
        // overwrite localStorage selection with the searchContainerService one
        // which maintains the original row order
        const rows = this.searchContainerService.selection;
        this.localStorage.mergeWithLocalGridState(this.gridStateKey, {
            selection: rows,
        });
        this.singleCheckBoxSelectEvent.emit({ data: rows });
        this.disabledMode = [false, rows.length !== 1, rows.length === 0, true];
        this.items = this.getMenuItems();
        this.configureButtons();
        this.translateLabels();
    }
    handleSetSearchFilters({ filters = {}, options = { clearResults: false, search: false }, }) {
        const searchTerm = this.searchForm.value[this.searchProperty];
        const _a = filters, _b = this.searchProperty, _ = _a[_b], otherFilters = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
        this.searchFilter = Object.assign({}, otherFilters, searchTerm !== '' && { [this.searchProperty]: searchTerm });
        if (options.clearResults) {
            this.clearResults();
        }
        if (options.search) {
            this.handleSearch();
        }
        this.filtersApplied = Object.keys(this.searchFilter).length > 0;
        this.localStorage.mergeWithLocalGridState(this.gridStateKey, {
            first: 0,
        });
        this.searchFilterUpdated.emit(this.searchFilter);
    }
    toggleAdvancedSearch() {
        this.showAdvancedSearch = !this.showAdvancedSearch;
    }
    // Private functions
    clearResults() {
        this.cleanup();
        this.clear.emit();
    }
    cleanup() {
        this.localStorage.mergeWithLocalGridState(this.gridStateKey, {
            selection: [],
        });
        this.searchContainerService.selection = [];
        this.handleSelection();
        this.reRenderGrid();
    }
    configureButtons() {
        this.menuItems = Object.assign({}, this.childButtons.reduce((menuItems, button) => {
            return Object.assign(Object.assign({}, menuItems), { [button.parent]: [
                    {
                        label: button.label,
                        disabled: button.disabled || this.disabledMode[button.mode],
                        command: () => this.onToolbarClick(button.name),
                    },
                    ...(menuItems[button.parent] || []),
                ] });
        }, Object.assign({}, this.columnMenuItems)));
    }
    getMenuItems() {
        return this.buttons.map(({ label, mode, name, disabled }) => ({
            label,
            disabled: disabled || this.disabledMode[mode],
            command: () => this.onToolbarClick(name),
        }));
    }
    reRenderGrid() {
        this.showGrid = false;
        setTimeout(() => {
            this.showGrid = true;
        });
    }
    routerGridStateKey() {
        const localKey = `${this.router.url.replace(/[/]/g, '_')}`;
        return localKey.indexOf('?') !== -1
            ? localKey.substring(0, localKey.indexOf('?')) + '_' + this.name
            : localKey + '_' + this.name;
    }
    getColumnChanges(selected) {
        const selectedColumnNames = selected.map((c) => c.field);
        const currentColumnNames = this.selectedColumns.map((c) => c.field);
        return [
            selectedColumnNames.filter((c) => !currentColumnNames.includes(c)),
            currentColumnNames.filter((c) => !selectedColumnNames.includes(c)),
        ];
    }
    handleColumnSelect({ selected }) {
        // set latest Column order
        this.columnOrder = this.localStorage.getProperty(this.gridStateKey, 'columnOrder');
        const [newColumns, deselectedColumns] = this.getColumnChanges(selected);
        if (newColumns.length > 0) {
            this.columnOrder = [...this.columnOrder, ...newColumns];
            this.columnWidths = [
                ...this.columnWidths.split(','),
                ...Array(newColumns.length).fill(250),
            ].toString();
            this.updateSelectedColumns();
        }
        if (deselectedColumns.length > 0) {
            this.columnOrder = this.columnOrder.filter((columnName) => !deselectedColumns.includes(columnName));
            this.localStorage.removeColumnWidths(this.gridStateKey, deselectedColumns);
            this.columnWidths = this.localStorage.getProperty(this.gridStateKey, 'columnWidths');
            this.updateSelectedColumns();
        }
        this.selectedColumns = this.columnOrder.map((columnName) => this.columns[this.columnMap.get(columnName)]);
    }
    handleResultsInputChange(results) {
        if (this.gridStateKey) {
            this.cleanup();
        }
        this.results = results;
    }
    labelToId(label) {
        return `sc-${label.replace(/[ _\.]/g, '-').toLocaleLowerCase()}`;
    }
    translateLabels() {
        this.subscriptions.push(this.translocoService
            .selectTranslate('SEARCH_CONTAINER')
            .pipe(map((translate) => translate), tap((translate) => {
            this.columnMenuItems.more = [
                {
                    label: translate.SELECT_COLUMNS,
                    command: () => this.toggleColumnDialog(),
                },
                {
                    label: translate.RESET_COLUMNS,
                    command: () => this.onResetClick(),
                },
            ];
        }))
            .subscribe());
    }
};
SearchContainerComponent.ctorParameters = () => [
    { type: FormBuilder },
    { type: Router },
    { type: LocalStorageService },
    { type: SearchContainerService },
    { type: TranslocoService }
];
__decorate([
    Input('buttons')
], SearchContainerComponent.prototype, "buttonsSetter", null);
__decorate([
    Input('columns')
], SearchContainerComponent.prototype, "columnsSetter", null);
__decorate([
    Input('columnsToDisplay')
], SearchContainerComponent.prototype, "columnsToDisplaySetter", null);
__decorate([
    Input()
], SearchContainerComponent.prototype, "disableAdvancedSearch", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "endPoint", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "i18nSearchField", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "idColumn", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "initialQuery", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "lazy", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "lazyLoadOnInit", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "loading", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "name", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "paging", void 0);
__decorate([
    Input('results')
], SearchContainerComponent.prototype, "resultsSetter", null);
__decorate([
    Input()
], SearchContainerComponent.prototype, "searchProperty", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "resetSelectionOnDestroy", void 0);
__decorate([
    Input()
], SearchContainerComponent.prototype, "clearSimpleSearch", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "clear", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "lazyLoad", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "paged", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "query", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "toolbar", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "field", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "singleCheckBoxSelectEvent", void 0);
__decorate([
    Output()
], SearchContainerComponent.prototype, "searchFilterUpdated", void 0);
__decorate([
    ContentChild(SearchDirective)
], SearchContainerComponent.prototype, "advSearch", void 0);
SearchContainerComponent = __decorate([
    Component({
        selector: 'lib-search-container',
        template: "<div class=\"sc-container flow-container vertical-flow ds\">\r\n  <!-- Simple Search placeholder -->\r\n  <div [formGroup]=\"searchForm\" class=\"vertical-flow sc-search\">\r\n    <div class=\"horizontal-flow\">\r\n      <input\r\n        pInputText\r\n        type=\"text\"\r\n        name=\"search-term\"\r\n        formControlName=\"{{ searchProperty }}\"\r\n        placeholder=\"{{\r\n          i18nSearchField || ('SEARCH_CONTAINER.SEARCH' | transloco)\r\n        }}\"\r\n        class=\"ui-inputtext sc-search-input\"\r\n        (keypress)=\"searchOnEnter($event)\"\r\n        data-testid=\"searchContainerSearchField\"\r\n      />\r\n      <div\r\n        class=\"horizontal-fixed form-controls space-before-125 space-between-050 sc-search-controls\"\r\n      >\r\n        <button\r\n          pButton\r\n          type=\"button\"\r\n          (click)=\"onClearClick()\"\r\n          label=\"{{ 'SEARCH_CONTAINER.CLEAR' | transloco }}\"\r\n          class=\"ds\"\r\n          data-testid=\"searchContainerClearButton\"\r\n        ></button>\r\n\r\n        <button\r\n          pButton\r\n          type=\"button\"\r\n          label=\"{{ 'SEARCH_CONTAINER.SEARCH' | transloco }}\"\r\n          (click)=\"onSearchClick()\"\r\n          class=\"ds\"\r\n          data-testid=\"searchContainerSearchButton\"\r\n        ></button>\r\n      </div>\r\n    </div>\r\n  </div>\r\n  <!-- End of Simple Search Form -->\r\n\r\n  <!-- Filter -->\r\n  <div class=\"space-before-350 sc-filter\">\r\n    <div\r\n      class=\"filter clickable sc-filter-button\"\r\n      *ngIf=\"!disableAdvancedSearch\"\r\n      [ngClass]=\"{ showing: showAdvancedSearch }\"\r\n      (click)=\"toggleAdvancedSearch()\"\r\n      data-testid=\"searchContainerFiltersButton\"\r\n    >\r\n      <i class=\"pi pi-sliders-h\"></i>\r\n      {{ 'SEARCH_CONTAINER.FILTER' | transloco }}\r\n    </div>\r\n    <div\r\n      *ngIf=\"!disableAdvancedSearch && filtersApplied\"\r\n      class=\"sc-filter-status\"\r\n      data-testid=\"searchContainerFiltersApplied\"\r\n    >\r\n      {{ 'SEARCH_CONTAINER.FILTERS_APPLIED' | transloco }}\r\n    </div>\r\n  </div>\r\n  <!-- End of Filter -->\r\n\r\n  <!-- Advanced Search/Search Toolbar/GRID container-->\r\n  <div class=\"sc-grid-area space-before-100\">\r\n    <div\r\n      class=\"sc-advanced-search\"\r\n      *ngIf=\"showAdvancedSearch && !disableAdvancedSearch\"\r\n    >\r\n      <ng-content select=\"lib-advanced-search\"></ng-content>\r\n      <ng-content select=\"lib-internal-advanced-search\"></ng-content>\r\n    </div>\r\n\r\n    <div class=\"vertical-flow sc-search-results\">\r\n      <div class=\"nav-secondary toolbar\">\r\n        <header class=\"component-header\">\r\n          {{ 'SEARCH_CONTAINER.RESULTS' | transloco }}\r\n        </header>\r\n\r\n        <div class=\"horizontal-flow space-between-025\">\r\n          <ng-container *ngFor=\"let button of buttons\">\r\n            <ng-container *ngIf=\"button?.items; else noSubMenu\">\r\n              <p-menu\r\n                #buttonSubMenu\r\n                [styleClass]=\"'more-menu'\"\r\n                appendTo=\"body\"\r\n                [popup]=\"true\"\r\n                [model]=\"button.items\"\r\n                data-testid=\"searchContainerToolbarMenu\"\r\n              ></p-menu>\r\n              <button\r\n                id=\"{{ labelToId(button.label) }}\"\r\n                pButton\r\n                class=\"ds tertiary small\"\r\n                label=\"{{ button.label }}\"\r\n                type=\"button\"\r\n                [disabled]=\"button.disabled || disabledMode[button.mode]\"\r\n                (click)=\"buttonSubMenu.toggle($event)\"\r\n                data-testid=\"searchContainerToolbarButton\"\r\n              ></button>\r\n            </ng-container>\r\n\r\n            <ng-template #noSubMenu>\r\n              <button\r\n                id=\"{{ labelToId(button.label) }}\"\r\n                pButton\r\n                class=\"ds tertiary small\"\r\n                label=\"{{ button.label }}\"\r\n                type=\"button\"\r\n                [disabled]=\"button.disabled || disabledMode[button.mode]\"\r\n                (click)=\"onToolbarClick(button.name)\"\r\n                data-testid=\"searchContainerToolbarButton\"\r\n              ></button>\r\n            </ng-template>\r\n          </ng-container>\r\n\r\n          <button\r\n            id=\"{{ labelToId('SEARCH_CONTAINER.MORE') }}\"\r\n            type=\"button\"\r\n            pButton\r\n            class=\"ds tertiary small\"\r\n            label=\"{{ 'SEARCH_CONTAINER.MORE' | transloco }}\"\r\n            (click)=\"moreMenu.toggle($event)\"\r\n            icon=\"pi pi-caret-right\"\r\n            data-testid=\"searchContainerToolbarButton\"\r\n          ></button>\r\n        </div>\r\n        <p-menu\r\n          #moreMenu\r\n          [styleClass]=\"'more-menu'\"\r\n          appendTo=\"body\"\r\n          [popup]=\"true\"\r\n          [model]=\"menuItems.more\"\r\n          data-testid=\"searchContainerToolbarMenu\"\r\n        ></p-menu>\r\n      </div>\r\n\r\n      <div class=\"space-before-025 grid\">\r\n        <lib-phoenix-grid\r\n          *ngIf=\"showGrid\"\r\n          [allowPaginator]=\"true\"\r\n          [allowReorderingOfColumns]=\"true\"\r\n          [allowResizingOfColumns]=\"true\"\r\n          [allowScroll]=\"true\"\r\n          [allowSelection]=\"true\"\r\n          [allowSingleColumnSorting]=\"true\"\r\n          [columns]=\"selectedColumns\"\r\n          [data$]=\"results\"\r\n          [first]=\"paging.page.first\"\r\n          [lazy]=\"lazy\"\r\n          [lazyLoadOnInit]=\"false\"\r\n          [loading]=\"loading\"\r\n          [records]=\"paging.records\"\r\n          [rows]=\"paging.page.rows\"\r\n          [rowsPerPageArray]=\"paging.sizes\"\r\n          [stateKey]=\"gridStateKey\"\r\n          (pageEvent)=\"handlePageEvent($event)\"\r\n          (selectAllCheckBoxEvent)=\"handleSelection()\"\r\n          (singleCheckBoxSelectEvent)=\"handleSelection()\"\r\n        ></lib-phoenix-grid>\r\n      </div>\r\n    </div>\r\n  </div>\r\n  <!-- End of Advanced Search/Search Toolbar/GRID container-->\r\n</div>\r\n<div class=\"sc-column-select-container\">\r\n  <p-dialog\r\n    [dismissableMask]=\"true\"\r\n    [formGroup]=\"selectedColumnsForm\"\r\n    [modal]=\"true\"\r\n    [showHeader]=\"false\"\r\n    [(visible)]=\"selectColumns\"\r\n    [resizable]=\"false\"\r\n    [styleClass]=\"'sc-column-select'\"\r\n    data-testid=\"selectColumnsDialog\"\r\n  >\r\n    <!-- Fix contentStyle above and styleClass below -->\r\n    <p-listbox\r\n      [options]=\"selectedColumnsOptions\"\r\n      formControlName=\"selected\"\r\n      multiple=\"multiple\"\r\n      checkbox=\"checkbox\"\r\n      filter=\"filter\"\r\n      [styleClass]=\"'width-100'\"\r\n      data-testid=\"selectColumnsList\"\r\n    >\r\n      <ng-template let-column pTemplate=\"item\">\r\n        <p>{{ column.label }}</p>\r\n      </ng-template>\r\n    </p-listbox>\r\n  </p-dialog>\r\n</div>\r\n",
        providers: [SearchContainerService, LocalStorageService]
    })
], SearchContainerComponent);

let PhoenixGridComponent = class PhoenixGridComponent {
    constructor(searchContainerService) {
        this.searchContainerService = searchContainerService;
        this.showFilter = false;
        this.pageEvent = new EventEmitter();
        this.saveGridSettingsEvent = new EventEmitter();
        this.singleCheckBoxSelectEvent = new EventEmitter();
        this.selectAllCheckBoxEvent = new EventEmitter();
        this.stateSaveEvent = new EventEmitter();
    }
    set data(data) {
        this.searchContainerService.data$.next(data);
    }
    get data() {
        return this.searchContainerService.data$.value;
    }
    emitPage($event) {
        this.pageEvent.emit($event);
    }
    handleLazyLoadEvent($event) {
        this.searchContainerService.onLazyLoad($event);
    }
    saveGridSettings() {
        this.saveGridSettingsEvent.emit();
    }
    singleCheckBoxSelect() {
        this.singleCheckBoxSelectEvent.emit();
    }
    selectAllCheckBox() {
        this.selectAllCheckBoxEvent.emit();
    }
    stateSave(event) {
        this.stateSaveEvent.emit(event);
    }
    ensureEvent(col, rowData) {
        return col.event === undefined ? null : col.event(rowData);
    }
};
PhoenixGridComponent.ctorParameters = () => [
    { type: SearchContainerService }
];
__decorate([
    Input()
], PhoenixGridComponent.prototype, "allowPaginator", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "allowReorderingOfColumns", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "allowResizingOfColumns", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "allowScroll", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "allowSelection", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "allowSingleColumnSorting", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "columns", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "data", null);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "lazy", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "lazyLoadOnInit", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "loading", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "paging", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "rows", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "first", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "records", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "rowsPerPageArray", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "showFilter", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "stateKey", void 0);
__decorate([
    Input()
], PhoenixGridComponent.prototype, "tableState", void 0);
__decorate([
    Output()
], PhoenixGridComponent.prototype, "pageEvent", void 0);
__decorate([
    Output()
], PhoenixGridComponent.prototype, "saveGridSettingsEvent", void 0);
__decorate([
    Output()
], PhoenixGridComponent.prototype, "singleCheckBoxSelectEvent", void 0);
__decorate([
    Output()
], PhoenixGridComponent.prototype, "selectAllCheckBoxEvent", void 0);
__decorate([
    Output()
], PhoenixGridComponent.prototype, "stateSaveEvent", void 0);
PhoenixGridComponent = __decorate([
    Component({
        selector: 'lib-phoenix-child-grid',
        template: "<div class=\"search-container-table-wrapper\">\r\n  <p-table\r\n    #dt\r\n    stateKey=\"{{ stateKey }}\"\r\n    stateStorage=\"local\"\r\n    styleClass=\"gridTableHeaderBorder search-container-table\"\r\n    class=\"search-container\"\r\n    [columns]=\"columns\"\r\n    [paginator]=\"allowPaginator\"\r\n    [totalRecords]=\"records || (data && data.length) || 0\"\r\n    [reorderableColumns]=\"allowReorderingOfColumns\"\r\n    [resizableColumns]=\"allowResizingOfColumns\"\r\n    [first]=\"first\"\r\n    [lazy]=\"lazy\"\r\n    [lazyLoadOnInit]=\"lazyLoadOnInit\"\r\n    [loading]=\"loading\"\r\n    [rows]=\"rows\"\r\n    [rowsPerPageOptions]=\"rowsPerPageArray\"\r\n    [value]=\"data\"\r\n    [scrollable]=\"allowScroll\"\r\n    [(selection)]=\"searchContainerService.selection\"\r\n    (onLazyLoad)=\"searchContainerService.onLazyLoad($event)\"\r\n    (onPage)=\"emitPage($event)\"\r\n    (onStateSave)=\"stateSave($event)\"\r\n    scrollHeight=\"flex\"\r\n  >\r\n    <!-- primeng required to allow scroll -->\r\n    <ng-template pTemplate=\"colgroup\" let-columns>\r\n      <colgroup>\r\n        <col />\r\n        <col *ngFor=\"let col of columns\" style=\"width: 250px\" />\r\n      </colgroup>\r\n    </ng-template>\r\n\r\n    <!-- No records template -->\r\n    <ng-template pTemplate=\"emptymessage\" let-columns>\r\n      <tr>\r\n        <td [attr.colspan]=\"columns.length\">\r\n          {{ 'COMMON.NO_RECORDS_FOUND' | transloco }}\r\n        </td>\r\n      </tr>\r\n    </ng-template>\r\n\r\n    <!--Header-->\r\n    <ng-template pTemplate=\"header\" let-columns>\r\n      <tr class=\"left\">\r\n        <ng-container *ngIf=\"allowSingleColumnSorting === true\">\r\n          <th *ngIf=\"allowSelection === true\" pResizableColumn>\r\n            <p-tableHeaderCheckbox\r\n              id=\"tableHeaderCheckbox\"\r\n              data-testid=\"searchContainerHeaderCheckbox\"\r\n              (click)=\"selectAllCheckBox()\"\r\n            ></p-tableHeaderCheckbox>\r\n          </th>\r\n          <th\r\n            *ngFor=\"let col of columns\"\r\n            pReorderableColumn\r\n            [pSortableColumn]=\"col.field\"\r\n            pResizableColumn\r\n            data-testid=\"searchContainerHeaderCell\"\r\n          >\r\n            {{ col.header }}\r\n          </th>\r\n        </ng-container>\r\n        <ng-container *ngIf=\"allowSingleColumnSorting === false\">\r\n          <th *ngFor=\"let col of columns\" pReorderableColumn pResizableColumn data-testid=\"searchContainerHeaderCell\">\r\n            {{ col.header }}\r\n          </th>\r\n        </ng-container>\r\n      </tr>\r\n      <!--Filters-->\r\n      <tr *ngIf=\"showFilter\" id=\"filteringOptionsRow\">\r\n        <th *ngIf=\"allowSelection\"></th>\r\n        <th *ngFor=\"let col of columns\" [ngSwitch]=\"col.field\">\r\n          <div *ngIf=\"col.type !== 'date'\">\r\n            <i class=\"pi pi-filter\" class=\"margin-right-0.5em\"></i>\r\n            <input\r\n              *ngSwitchCase=\"col.field\"\r\n              pInputText\r\n              type=\"{{ col.type }}\"\r\n              placeholder=\"Filter By {{ col.header }}\"\r\n              (input)=\"\r\n                dt.filter($event.target.value, col.field, col.filterMatchMode)\r\n              \"\r\n            />\r\n          </div>\r\n          <div *ngIf=\"col.type === 'date'\">\r\n            <p-calendar\r\n              placeholder=\"Filter By {{ col.header }} range\"\r\n              [(ngModel)]=\"rangeDates\"\r\n              [showIcon]=\"true\"\r\n              appendTo=\"body\"\r\n              dateFormat=\"yy-mm-dd\"\r\n              [showButtonBar]=\"true\"\r\n              [showOtherMonths]=\"false\"\r\n              (onSelect)=\"dt.filter($event.value, col.field, 'dateRangeFilter')\"\r\n              [monthNavigator]=\"true\"\r\n              [yearNavigator]=\"true\"\r\n              yearRange=\"1980:2030\"\r\n              selectionMode=\"range\"\r\n              [readonlyInput]=\"true\"\r\n            >\r\n            </p-calendar>\r\n          </div>\r\n        </th>\r\n      </tr>\r\n    </ng-template>\r\n\r\n    <!--Row-->\r\n    <ng-template pTemplate=\"body\" let-rowData let-columns=\"columns\">\r\n      <tr id=\"AllowSelectionCheckBoxBodyTrue\">\r\n        <td *ngIf=\"allowSelection\">\r\n          <p-tableCheckbox\r\n            id=\"tableBodyCheckbox\"\r\n            data-testid=\"searchContainerRowCheckbox\"\r\n            [value]=\"rowData\"\r\n            (click)=\"singleCheckBoxSelect()\"\r\n          ></p-tableCheckbox>\r\n        </td>\r\n        <ng-container *ngFor=\"let col of columns\">\r\n          <td>\r\n            <lib-column [data]=\"rowData\" [column]=\"col\" data-testid=\"searchContainerRowCell\"></lib-column>\r\n          </td>\r\n        </ng-container>\r\n      </tr>\r\n    </ng-template>\r\n  </p-table>\r\n</div>\r\n",
        styles: [""]
    })
], PhoenixGridComponent);

let ColumnComponent = class ColumnComponent {
    constructor() { }
    ngOnInit() {
        const types = ['link', 'imgSrc', 'formatter', 'icon'];
        this.template = Object.keys(this.column).reduce((hasTemplate, key) => {
            return hasTemplate || types.includes(key);
        }, false);
    }
};
__decorate([
    Input()
], ColumnComponent.prototype, "column", void 0);
__decorate([
    Input()
], ColumnComponent.prototype, "data", void 0);
ColumnComponent = __decorate([
    Component({
        selector: 'lib-column',
        template: `
    <lib-layout
      *ngIf="template; else noTemplate"
      [config]="column"
      [data]="data"
    ></lib-layout>
    <ng-template #noTemplate>
      {{ data[column.field] }}
    </ng-template>
  `
    })
], ColumnComponent);

let LayoutComponent = class LayoutComponent {
    constructor(searchContainerService) {
        this.searchContainerService = searchContainerService;
        this.has = { link: false, img: false, formatter: false, icon: false };
    }
    ngOnInit() {
        this.keys = Object.keys(this.config);
        if (this.keys.includes('link')) {
            this.has.link = true;
            const link = this.config.link(this.data);
            this.path = link.path;
            this.queryParams = link.queryParams;
        }
        if (this.keys.includes('imgSrc')) {
            this.has.img = true;
            const imageSrc = this.config.imgSrc(this.data);
            this.url = imageSrc ? imageSrc.dataUrl : null;
            if (!this.noLink('imgSrc')) {
                this.template = 'imgSrc';
            }
        }
        if (this.keys.includes('formatter')) {
            this.has.formatter = true;
            this.field = this.config.formatter(this.data);
            if (!this.noLink('formatter')) {
                this.template = 'formatter';
            }
        }
        if (this.keys.includes('icon')) {
            this.has.icon = true;
            if (!this.noLink('icon')) {
                this.template = 'icon';
            }
            const warningsData = this.data[this.config.field];
            if (warningsData === 0) {
                this.template = 'empty';
            }
        }
        this.shouldRender = this.config.shouldRender
            ? this.config.shouldRender(this.data)
            : true;
    }
    ensureEvent(col, rowData) {
        return col.event === undefined ? null : col.event(rowData);
    }
    noLink(template) {
        return this.keys.includes(template) && !this.keys.includes('link');
    }
};
LayoutComponent.ctorParameters = () => [
    { type: SearchContainerService }
];
__decorate([
    Input()
], LayoutComponent.prototype, "data", void 0);
__decorate([
    Input()
], LayoutComponent.prototype, "config", void 0);
LayoutComponent = __decorate([
    Component({
        selector: 'lib-layout',
        template: `
    <ng-container *ngIf="shouldRender">
      <ng-container *ngIf="has.link">
        <ng-container *ngIf="path && path !== './'; else noPath">
          <a
            [ngClass]="config.styleClass"
            routerLink="{{ path }}"
            [queryParams]="queryParams"
            (click)="
              searchContainerService.onFieldEvent(ensureEvent(config, data))
            "
          >
            <ng-container [ngSwitch]="template">
              <ng-container *ngSwitchCase="'imgSrc'">
                <div class="img-container grid-thumbnail">
                  <img
                    class="target"
                    [src]="url || 'assets/images/no-thumbnail.svg'"
                  />
                </div>
              </ng-container>
              <ng-container *ngSwitchCase="'formatter'">
                {{ field }}
              </ng-container>
              <ng-container *ngSwitchCase="'icon'">
                <i class="pi pi-{{ config.icon }}"></i>
              </ng-container>
              <ng-container *ngSwitchCase="'empty'"> </ng-container>
              <ng-container *ngSwitchDefault>
                {{ data[config.field] }}
              </ng-container>
            </ng-container>
          </a>
        </ng-container>
        <ng-template #noPath>
          <a
            [ngClass]="config.styleClass"
            (click)="
              searchContainerService.onFieldEvent(ensureEvent(config, data))
            "
          >
            <ng-container [ngSwitch]="template">
              <ng-container *ngSwitchCase="'imgSrc'">
                <div class="img-container grid-thumbnail">
                  <img
                    class="target"
                    [src]="url || 'assets/images/no-thumbnail.svg'"
                  />
                </div>
              </ng-container>
              <ng-container *ngSwitchCase="'formatter'">
                {{ field }}
              </ng-container>
              <ng-container *ngSwitchCase="'icon'">
                <i class="pi pi-{{ config.icon }}"></i>
              </ng-container>
              <ng-container *ngSwitchCase="'empty'"> </ng-container>
              <ng-container *ngSwitchDefault>
                {{ data[config.field] }}
              </ng-container>
            </ng-container>
          </a>
        </ng-template>
      </ng-container>
      <ng-container *ngIf="noLink('imgSrc')">
        <div class="img-container grid-thumbnail">
          <img class="target" [src]="url || 'assets/images/no-thumbnail.svg'" />
        </div>
      </ng-container>
      <ng-container *ngIf="noLink('formatter')">
        {{ field }}
      </ng-container>
    </ng-container>
  `
    })
], LayoutComponent);

let GridService = class GridService {
    setGridStorage(stateKey, rowsPerPageArray) {
        const localStorageStateForThisStateKey = this.getDataFromLocalStorage(stateKey);
        const dataForStorage = Object.assign({}, {
            first: 0,
            rows: rowsPerPageArray[0],
            selection: [],
        }, localStorageStateForThisStateKey);
        this.setLocalStorage(stateKey, dataForStorage);
    }
    updateLocalStorageToReflectColumnChanges(stateKey, selectedColumns) {
        const currentListOfSelectedColumns = selectedColumns.map((columns) => columns.field);
        const localStorageStateForThisStateKey = this.getDataFromLocalStorage(stateKey);
        const dataForStorage = Object.assign({}, localStorageStateForThisStateKey, {
            columnOrder: currentListOfSelectedColumns,
        });
        this.setLocalStorage(stateKey, dataForStorage);
    }
    setLocalStorage(stateKey, dataForStorage) {
        localStorage.setItem(stateKey, JSON.stringify(dataForStorage));
    }
    getDataFromLocalStorage(StateKey) {
        return JSON.parse(localStorage.getItem(StateKey));
    }
    getSelectionArrayFromLocalStorage(stateKey) {
        const localStorageStateForThisStateKey = this.getDataFromLocalStorage(stateKey);
        return localStorageStateForThisStateKey.selection;
    }
};
GridService = __decorate([
    Injectable()
], GridService);

let PhoenixGridContainerComponent = class PhoenixGridContainerComponent {
    constructor(gridService) {
        this.gridService = gridService;
        this.allowPaginator = false;
        this.allowReorderingOfColumns = false;
        this.allowResizingOfColumns = false;
        this.allowScroll = false;
        this.allowSelection = false;
        this.allowSingleColumnSorting = false;
        this.showFilter = false;
        this.pageEvent = new EventEmitter();
        this.stateSaveEvent = new EventEmitter();
        this.saveGridStateEvent = new EventEmitter();
        this.selectAllCheckBoxEvent = new EventEmitter();
        this.singleCheckBoxSelectEvent = new EventEmitter();
        this.selectedColumns = undefined;
    }
    set columns(value) {
        this.selectedColumns = value;
        this.handleColumnsChanged(this.selectedColumns);
    }
    ngOnInit() {
        this.gridService.setGridStorage(this.stateKey, this.rowsPerPageArray);
    }
    handlePageEvent($event) {
        this.pageEvent.emit($event);
    }
    handleSaveGridSettings() {
        this.saveGridStateEvent.emit(this.stateKey);
    }
    handleColumnsChanged(selectedColumns) {
        if (this.stateKey) {
            this.gridService.updateLocalStorageToReflectColumnChanges(this.stateKey, selectedColumns);
        }
    }
    selectAllCheckBox() {
        this.selectAllCheckBoxEvent.emit();
    }
    singleCheckBoxSelect() {
        this.singleCheckBoxSelectEvent.emit();
    }
};
PhoenixGridContainerComponent.ctorParameters = () => [
    { type: GridService }
];
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "columns", null);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "allowPaginator", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "allowReorderingOfColumns", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "allowResizingOfColumns", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "allowScroll", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "allowSelection", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "allowSingleColumnSorting", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "data$", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "lazy", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "lazyLoadOnInit", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "loading", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "paging", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "rows", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "first", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "records", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "rowsPerPageArray", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "showFilter", void 0);
__decorate([
    Input()
], PhoenixGridContainerComponent.prototype, "stateKey", void 0);
__decorate([
    Output()
], PhoenixGridContainerComponent.prototype, "pageEvent", void 0);
__decorate([
    Output()
], PhoenixGridContainerComponent.prototype, "stateSaveEvent", void 0);
__decorate([
    Output()
], PhoenixGridContainerComponent.prototype, "saveGridStateEvent", void 0);
__decorate([
    Output()
], PhoenixGridContainerComponent.prototype, "selectAllCheckBoxEvent", void 0);
__decorate([
    Output()
], PhoenixGridContainerComponent.prototype, "singleCheckBoxSelectEvent", void 0);
PhoenixGridContainerComponent = __decorate([
    Component({
        selector: 'lib-phoenix-grid',
        template: "<lib-phoenix-child-grid\r\n  [allowPaginator]=\"allowPaginator\"\r\n  [allowReorderingOfColumns]=\"allowReorderingOfColumns\"\r\n  [allowResizingOfColumns]=\"allowResizingOfColumns\"\r\n  [allowScroll]=\"allowScroll\"\r\n  [allowSelection]=\"allowSelection\"\r\n  [allowSingleColumnSorting]=\"allowSingleColumnSorting\"\r\n  [columns]=\"selectedColumns\"\r\n  [data]=\"data$ | async\"\r\n  [lazy]=\"lazy\"\r\n  [lazyLoadOnInit]=\"lazyLoadOnInit\"\r\n  [loading]=\"loading\"\r\n  [first]=\"first\"\r\n  [records]=\"records\"\r\n  [rows]=\"rows\"\r\n  [rowsPerPageArray]=\"rowsPerPageArray\"\r\n  [showFilter]=\"showFilter\"\r\n  [stateKey]=\"stateKey\"\r\n  (pageEvent)=\"handlePageEvent($event)\"\r\n  (selectAllCheckBoxEvent)=\"selectAllCheckBox()\"\r\n  (singleCheckBoxSelectEvent)=\"singleCheckBoxSelect()\"\r\n  (saveGridSettingsEvent)=\"handleSaveGridSettings()\"\r\n>\r\n</lib-phoenix-child-grid>\r\n",
        providers: [GridService],
        styles: [""]
    })
], PhoenixGridContainerComponent);

let SearchContainerModule = class SearchContainerModule {
};
SearchContainerModule = __decorate([
    NgModule({
        declarations: [
            PhoenixGridContainerComponent,
            PhoenixGridComponent,
            SearchContainerComponent,
            SearchDirective,
            LayoutComponent,
            ColumnComponent,
        ],
        imports: [
            CalendarModule,
            CheckboxModule,
            CommonModule,
            ConfirmDialogModule,
            DialogModule,
            FormsModule,
            ListboxModule,
            MenuModule,
            MultiSelectModule,
            ReactiveFormsModule,
            RouterModule,
            TableModule,
            TranslocoModule,
        ],
        exports: [SearchContainerComponent, SearchDirective],
        providers: [ConfirmationService],
    })
], SearchContainerModule);

const defaultState = {
    filters: {},
    toolbarButtons: {},
    columns: {},
    paging: {
        sizes: [25, 50, 75, 100],
        page: { first: 0, rows: 25 },
    },
};

const searchFilters = (term = '', pagination, advancedSearch) => {
    const page = pagination !== null && pagination !== undefined ? pagination : {};
    const search = {
        search: {
            term,
            filters: advancedSearch,
        },
        sort: {},
        page: {
            limit: page.rows ? page.rows : 25,
            offset: page.first ? page.first : 0,
        },
    };
    Object.assign(search, page.sortField && {
        sort: { field: page.sortField, order: page.sortOrder },
    });
    return search;
};

var HttpMethod;
(function (HttpMethod) {
    HttpMethod["DELETE"] = "DELETE";
    HttpMethod["GET"] = "GET";
    HttpMethod["POST"] = "POST";
    HttpMethod["PUT"] = "PUT";
})(HttpMethod || (HttpMethod = {}));
let BaseHttpService = class BaseHttpService {
    constructor(http) {
        this.http = http;
        this._baseUrl = '';
    }
    set baseUrl(baseUrl) {
        this._baseUrl = baseUrl;
    }
    get baseUrl() {
        return this._baseUrl;
    }
    fetchWithFqn({ endpoint, httpMethod, body, responseField, options, }) {
        return this.buildRequest(endpoint, httpMethod, body, options).pipe(map((response) => responseField && response ? response[responseField] : response));
    }
    fetch({ endpoint, httpMethod, body, responseField, options, }) {
        return this.buildRequest(urlJoin(this._baseUrl, endpoint), httpMethod, body, options).pipe(map((response) => responseField && response ? response[responseField] : response));
    }
    buildRequest(endpoint, httpMethod, body, options) {
        switch (httpMethod) {
            case HttpMethod.GET:
                return this.http.get(endpoint, Object.assign(Object.assign({}, options), { withCredentials: true }));
            case HttpMethod.POST:
                return this.http.post(endpoint, body, Object.assign(Object.assign({}, options), { withCredentials: true }));
            case HttpMethod.PUT:
                return this.http.put(endpoint, body, Object.assign(Object.assign({}, options), { withCredentials: true }));
            case HttpMethod.DELETE:
                return this.http.delete(endpoint, Object.assign(Object.assign({}, options), { withCredentials: true }));
        }
    }
};
BaseHttpService.ctorParameters = () => [
    { type: HttpClient }
];
BaseHttpService.ɵprov = ɵɵdefineInjectable({ factory: function BaseHttpService_Factory() { return new BaseHttpService(ɵɵinject(HttpClient)); }, token: BaseHttpService, providedIn: "root" });
BaseHttpService = __decorate([
    Injectable({
        providedIn: 'root',
    })
], BaseHttpService);

let SearchContainerLoaderService = class SearchContainerLoaderService {
    constructor(http) {
        this.http = http;
        this._searchError = new Subject();
        this._searchConfigError = new Subject();
        this.paging = {
            sizes: [25, 50, 75, 100],
            page: { first: 0, rows: 25 },
        };
        this.fetching = false;
        this.data$ = of([]);
        this.searchError$ = this._searchError.asObservable();
        this.searchConfigError$ = this._searchConfigError.asObservable();
        this.baseUrl = '';
    }
    searchConfigError() {
        this._searchConfigError.next();
    }
    search(_a) {
        var _b = _a.filters, { term } = _b, rest = __rest(_b, ["term"]), pagination = __rest(_a, ["filters"]);
        this.sort = {
            field: pagination.sortField,
            order: pagination.sortOrder,
        };
        this.term = term;
        this.pagination = pagination;
        this.filters = rest;
        this.data$ = this.getPaginatedData(searchFilters(term, pagination, this.filters));
    }
    clear() {
        this.term = '';
        this.data$ = this.getPaginatedData(searchFilters(this.term, {
            first: 0,
            rows: 25,
            sortField: this.sort.field,
            sortOrder: this.sort.order,
        }, this.filters));
    }
    getPaginatedData(filters) {
        this.fetching = true;
        return this.http
            .fetchWithFqn({
            endpoint: `${this.baseUrl}/search`,
            httpMethod: 'POST',
            body: filters,
        })
            .pipe(tap((connections) => {
            this.paging = Object.assign(Object.assign({}, this.paging), { records: connections.totalRecords });
        }), map((connectionTypesResponse) => {
            return connectionTypesResponse.records.map((connectionTypes) => {
                return connectionTypes;
            });
        }), catchError(() => of(this._searchError.next())), finalize(() => (this.fetching = false)));
    }
    init() {
        return this.http
            .fetchWithFqn({
            endpoint: `${this.baseUrl}/search`,
            httpMethod: 'GET',
        })
            .pipe(map((response) => {
            return Object.assign(Object.assign({}, defaultState), response);
        }));
    }
    refreshSearch() {
        this.search(Object.assign({ filters: Object.assign({ term: this.term }, this.filters) }, this.pagination));
    }
    getCurrentSearchDetails() {
        return Object.assign({ filters: Object.assign({ term: this.term }, this.filters) }, this.pagination);
    }
};
SearchContainerLoaderService.ctorParameters = () => [
    { type: BaseHttpService }
];
SearchContainerLoaderService = __decorate([
    Injectable()
], SearchContainerLoaderService);

let SearchContainerLoaderComponent = class SearchContainerLoaderComponent {
    constructor(route, router, searchContainerLoaderService, searchContainerService, store) {
        this.route = route;
        this.router = router;
        this.searchContainerLoaderService = searchContainerLoaderService;
        this.searchContainerService = searchContainerService;
        this.store = store;
        this.disableAdvancedSearch = false;
        this.hasWarningsColumn = false;
        this.searchContainerName = '';
        this.i18nSearchField = '';
        this.params = {};
        this.columnActions = {};
        this.baseUrl = '';
        this.writeFiltersToUrl = false;
        this.field = new EventEmitter();
        this.toolbarButtons = [];
        this.columns = [];
        this.columnsToDisplay = [];
        this.columnsAvailable = false;
        this.initialQuery = {};
        this.facetData = new BehaviorSubject([]);
        this.facetData$ = this.facetData.asObservable();
        this.dateType = {
            format: '',
            description: '',
            primeNgFormat: '',
        };
        this._shouldDestroy = new Subject();
    }
    transformColumns(columns) {
        return Object.entries(columns).reduce((cols, [name, config]) => {
            return [
                ...cols,
                Object.assign({ field: name, header: config.label, required: config.isRequired }, this.columnActions[name]),
            ];
        }, []);
    }
    transformMenuButton(name, button, parent = '') {
        return button.items
            ? Object.assign(Object.assign({ name }, button), { items: Object.entries(button.items).map(([itemName, itemButton]) => this.transformMenuButton(itemName, itemButton, name)) }) : Object.assign(Object.assign({ name }, button), { parent });
    }
    transformMenuButtons(toolbarButtons) {
        return Object.entries(toolbarButtons).map(([name, button]) => this.transformMenuButton(name, button));
    }
    transformLegacyButtons(buttons) {
        var _a;
        const more = buttons.filter((button) => button.name === 'more')[0];
        const rest = buttons.filter((button) => button.name !== 'more');
        return buttons.length === 0 ? [] : [...rest, ...((_a = more === null || more === void 0 ? void 0 : more.items) !== null && _a !== void 0 ? _a : [])];
    }
    transformDefaultSort(defaultSort) {
        return {
            sortField: defaultSort.field,
            sortOrder: defaultSort.order,
        };
    }
    transformColumnsToDisplay(columns) {
        return Object.entries(columns).reduce((columnsToDisplay, [name, config]) => {
            return config.isDefault
                ? [...columnsToDisplay, name]
                : columnsToDisplay;
        }, []);
    }
    transformFacetData() {
        this.facetData.next(Object.entries(this.facetsConfig || {}).map(([name, value]) => (Object.assign(Object.assign({}, value), { name }))));
    }
    ngOnInit() {
        this.route.queryParams
            .pipe(tap((params) => {
            this.params = JSON.parse((params === null || params === void 0 ? void 0 : params.filters) || '{}');
        }), takeUntil(this._shouldDestroy.asObservable()))
            .subscribe();
        this.searchContainerLoaderService.baseUrl = this.baseUrl;
        this.searchContainerLoaderService
            .init()
            .pipe(tap(({ defaultSort, toolbarButtons, columns, filters }) => {
            this.searchContainerLoaderService.paging = Object.assign(Object.assign({}, this.searchContainerLoaderService.paging), this.transformDefaultSort(defaultSort));
            this.columnsToDisplay = this.transformColumnsToDisplay(columns);
            this.columns = this.transformColumns(columns);
            this.toolbarButtons = this.transformLegacyButtons(this.transformMenuButtons(toolbarButtons || {}));
            this.facetsConfig = this.transformConfig(filters);
            this.transformFacetData();
            this.columnsAvailable = true;
        }), catchError(() => of(this.searchContainerLoaderService.searchConfigError())), takeUntil(this._shouldDestroy.asObservable()))
            .subscribe();
        this.searchContainerService.hasWarningsColumn = this.hasWarningsColumn;
        this.store
            .pipe(select(createSelector(createFeatureSelector('user'), (state) => state.profile)), tap((user) => {
            var _a, _b, _c, _d, _e, _f;
            return (this.dateType = {
                primeNgFormat: (_b = (_a = user.userInfo) === null || _a === void 0 ? void 0 : _a.PrimeNGDateTypeFormat) !== null && _b !== void 0 ? _b : this.dateType.primeNgFormat,
                format: (_d = (_c = user.userInfo) === null || _c === void 0 ? void 0 : _c.DateTypeFormat) !== null && _d !== void 0 ? _d : this.dateType.format,
                description: (_f = (_e = user.userInfo) === null || _e === void 0 ? void 0 : _e.DateTypeDescription) !== null && _f !== void 0 ? _f : this.dateType.description,
            });
        }), takeUntil(this._shouldDestroy))
            .subscribe();
        this.store
            .pipe(select(createSelector(createFeatureSelector('user'), (state) => state.profile)), tap((user) => {
            var _a, _b;
            this.languageCode = (_b = (_a = user === null || user === void 0 ? void 0 : user.userInfo) === null || _a === void 0 ? void 0 : _a.LanguageCode) !== null && _b !== void 0 ? _b : navigator.language;
        }), takeUntil(this._shouldDestroy))
            .subscribe();
    }
    transformConfig(filters) {
        return Object.entries(filters).reduce((config, [name, filterConfig]) => {
            const _a = filterConfig, _b = filterConfig.type, configType = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
            return Object.assign(Object.assign({}, config), { [name]: Object.assign(Object.assign({}, rest), { value: configType === null || configType === void 0 ? void 0 : configType.options }) });
        }, {});
    }
    onToolbarClick({ command, selection, clearSelection }) {
        this.toolbarActionsDispatcher[command](selection, clearSelection);
    }
    ngOnDestroy() {
        this._shouldDestroy.next(true);
    }
    filterParams(filters) {
        if (this.writeFiltersToUrl) {
            this.router.navigate([], {
                queryParams: Object.keys(filters).length !== 0
                    ? { filters: JSON.stringify(filters) }
                    : {},
                relativeTo: this.route,
            });
        }
    }
};
SearchContainerLoaderComponent.ctorParameters = () => [
    { type: ActivatedRoute },
    { type: Router },
    { type: SearchContainerLoaderService },
    { type: SearchContainerService },
    { type: Store }
];
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "disableAdvancedSearch", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "hasWarningsColumn", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "searchContainerName", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "i18nSearchField", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "toolbarActionsDispatcher", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "params", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "columnActions", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "baseUrl", void 0);
__decorate([
    Input()
], SearchContainerLoaderComponent.prototype, "writeFiltersToUrl", void 0);
__decorate([
    Output()
], SearchContainerLoaderComponent.prototype, "field", void 0);
SearchContainerLoaderComponent = __decorate([
    Component({
        selector: 'lib-search-container-loader',
        template: "<div style=\"display: flex; flex-direction: column; row-gap: 2em\">\r\n  <lib-search-container\r\n    *ngIf=\"columnsAvailable; else columnsNotAvailable\"\r\n    i18nSearchField=\"{{ i18nSearchField | transloco }}\"\r\n    [name]=\"searchContainerName\"\r\n    searchProperty=\"key\"\r\n    [buttons]=\"toolbarButtons\"\r\n    [loading]=\"searchContainerLoaderService.fetching\"\r\n    [columns]=\"columns\"\r\n    [columnsToDisplay]=\"columnsToDisplay\"\r\n    [paging]=\"searchContainerLoaderService.paging\"\r\n    [results]=\"searchContainerLoaderService.data$\"\r\n    [searchProperty]=\"'term'\"\r\n    (clear)=\"searchContainerLoaderService.clear()\"\r\n    (query)=\"searchContainerLoaderService.search($event)\"\r\n    (toolbar)=\"onToolbarClick($event)\"\r\n    [lazy]=\"true\"\r\n    (lazyLoad)=\"searchContainerLoaderService.search($event)\"\r\n    (field)=\"field.emit($event)\"\r\n    [lazyLoadOnInit]=\"disableAdvancedSearch\"\r\n    [initialQuery]=\"initialQuery\"\r\n    [disableAdvancedSearch]=\"disableAdvancedSearch\"\r\n    data-testid=\"searchContainer\"\r\n  >\r\n    <lib-advanced-search\r\n      libSearch\r\n      [facetsConfig]=\"facetsConfig\"\r\n      [facetData]=\"facetData$ | async\"\r\n      [params]=\"params\"\r\n      [dateFormat]=\"dateType\"\r\n      [languageCode]=\"languageCode\"\r\n      data-testid=\"searchContainerFilters\"\r\n      (filterQueryParams)=\"filterParams($event)\"\r\n    >\r\n    </lib-advanced-search>\r\n  </lib-search-container>\r\n\r\n  <ng-template #columnsNotAvailable>\r\n    <div class=\"position-relative\" style=\"min-height: 50vh\">\r\n      <lib-working-spinner-component\r\n        [visible]=\"true\"\r\n      ></lib-working-spinner-component>\r\n    </div>\r\n  </ng-template>\r\n  <p-confirmDialog [closeOnEscape]=\"false\" [closable]=\"false\"></p-confirmDialog>\r\n</div>\r\n",
        providers: [BaseHttpService]
    })
], SearchContainerLoaderComponent);

const defaultState$1 = {
    facets: [],
};
const ɵ0 = (_, { facets }) => ({
    facets: [...facets, false],
}), ɵ1 = (index, { facets }) => ({
    facets: Object.assign([], facets, { [index]: !facets[index] }),
});
const schema = {
    addFacet: {
        command: ɵ0,
    },
    toggleFacet: {
        command: ɵ1,
    },
};

const getWeekdayNameOptionsDefaults = {
    startDay: 0,
    format: 'short',
    locale: navigator.language,
    todayLabel: 'Today',
    clearLabel: 'Clear',
};
const TE_ST_LOCALE = {
    firstDayOfWeek: 0,
    dayNames: [
        '*Sunday',
        '*Monday',
        '*Tuesday',
        '*Wednesday',
        '*Thursday',
        '*Friday',
        '*Saturday',
    ],
    dayNamesShort: ['*Sun', '*Mon', '*Tue', '*Wed', '*Thu', '*Fri', '*Sat'],
    dayNamesMin: ['*S', '*M', '*T', '*W', '*T', '*F', '*S'],
    monthNames: [
        '*January',
        '*February',
        '*March',
        '*April',
        '*May',
        '*June',
        '*July',
        '*August',
        '*September',
        '*October',
        '*November',
        '*December',
    ],
    monthNamesShort: [
        '*Jan',
        '*Feb',
        '*Mar',
        '*Apr',
        '*May',
        '*Jun',
        '*Jul',
        '*Aug',
        '*Sep',
        '*Oct',
        '*Nov',
        '*Dec',
    ],
};
const range = (start, end) => {
    return [...Array(end - start + 1).keys()].map((k) => k + start);
};
const getWeekdayNames = (options = {}) => {
    const referenceDate = new Date(1978, 11, 31); // This date is a Sunday
    const mergedOptions = Object.assign(Object.assign({}, getWeekdayNameOptionsDefaults), options);
    return range(0, 6).map((day) => {
        return add(referenceDate, { days: day }).toLocaleDateString(mergedOptions.locale, { weekday: mergedOptions.format });
    });
};
const getMonthNames = (options = {}) => {
    const referenceDate = new Date(1978, 0, 1); // This date is a January
    const mergedOptions = Object.assign(Object.assign({}, getWeekdayNameOptionsDefaults), options);
    return range(0, 11).map((month) => {
        return add(referenceDate, { months: month }).toLocaleDateString(mergedOptions.locale, { month: mergedOptions.format });
    });
};
const firstDayMap = {
    // North America
    'en-us': 0,
    'en-ca': 0,
    'fr-ca': 1,
    'es-mx': 0,
    // Europe (mostly Monday)
    'en-gb': 1,
    'fr-fr': 1,
    'de-de': 1,
    'es-es': 1,
    'it-it': 1,
    'pt-pt': 1,
    'nl-nl': 1,
    'da-dk': 1,
    'sv-se': 1,
    'fi-fi': 1,
    'no-no': 1,
    'pl-pl': 1,
    'cs-cz': 1,
    'sk-sk': 1,
    'hu-hu': 1,
    'ru-ru': 1,
    'uk-ua': 1,
    // Middle East & Islamic world (Saturday or Sunday)
    'ar-eg': 6,
    'ar-sa': 6,
    'he-il': 0,
    // Asia
    'ja-jp': 1,
    'zh-cn': 1,
    'zh-tw': 0,
    'ko-kr': 1,
    'hi-in': 1,
    'th-th': 1,
    'vi-vn': 1,
    // Oceania
    'en-au': 1,
    'en-nz': 1,
    // Africa
    'en-za': 1,
    'sw-ke': 1,
    'yo-ng': 1,
    // South America (mostly Sunday)
    'pt-br': 0,
    'es-ar': 0,
    'es-cl': 0,
    'es-co': 0,
    'es-pe': 0,
};
const getFirstDayOfWeek = (locale) => {
    var _a;
    return (_a = firstDayMap[locale.toLowerCase()]) !== null && _a !== void 0 ? _a : 0; // default to Sunday
};
const buildLocale = (options = {}) => {
    const mergedOptions = Object.assign(Object.assign({}, getWeekdayNameOptionsDefaults), options);
    switch (mergedOptions.locale) {
        case 'te-st':
            return Object.assign(Object.assign({}, TE_ST_LOCALE), { today: mergedOptions.todayLabel, clear: mergedOptions.clearLabel });
        default:
            return {
                firstDayOfWeek: getFirstDayOfWeek(mergedOptions.locale),
                dayNames: getWeekdayNames(Object.assign(Object.assign({}, mergedOptions), { format: 'long' })),
                dayNamesShort: getWeekdayNames(Object.assign(Object.assign({}, mergedOptions), { format: 'short' })),
                dayNamesMin: getWeekdayNames(Object.assign(Object.assign({}, mergedOptions), { format: 'narrow' })),
                monthNames: getMonthNames(Object.assign(Object.assign({}, mergedOptions), { format: 'long' })),
                monthNamesShort: getMonthNames(Object.assign(Object.assign({}, mergedOptions), { format: 'short' })),
                today: mergedOptions.todayLabel,
                clear: mergedOptions.clearLabel,
            };
    }
};

let AdvancedSearchLoaderComponent = class AdvancedSearchLoaderComponent {
    constructor(actionUi) {
        this.actionUi = actionUi;
        this.setFilterEvent = new EventEmitter();
        this.selectedYearChanged = new EventEmitter();
        this.filterQueryParams = new EventEmitter();
        this.filtersParam = {};
        this.subscriptions = [];
        this.appliedIcon = 'assets/images/filter-on.png';
        this.selectedListItems = {};
        this.selectedTextItems = {};
        this.selectedRangeItems = {};
        this.selectedRadioItems = {};
        this.selectedTreeItems = {};
        this.selectedTreeNodes = {};
        this.locale = null;
        this.actions = Action.createDispatcher(schema);
        this.actionUi.initialize(defaultState$1, this.actions);
        this.actionUi.state
            .pipe(tap((state) => (this.state = Object.assign({}, state))), takeUntil(this.actionUi.shouldDestroy))
            .subscribe();
    }
    set facetDataSetter(facetData) {
        this.filters = this.transformFacetData(facetData).sort((a, b) => a.label > b.label ? 1 : -1);
    }
    set paramsSetter(params) {
        var _a;
        if ((params === null || params === void 0 ? void 0 : params.Year) && ((_a = this.params) === null || _a === void 0 ? void 0 : _a.Year) !== (params === null || params === void 0 ? void 0 : params.Year)) {
            this.selectedYearChanged.emit(params.Year);
        }
        this.params = params;
        this.buildFacets();
    }
    ngOnInit() {
        this.subscriptions.push(timer(0).subscribe(() => {
            this.handleAdvancedSearch();
        }));
        this.locale = buildLocale({
            locale: this.languageCode,
        });
    }
    getValidDate($event, facetName) {
        this.selectedRangeItems[facetName] = $event;
    }
    getParam(name) {
        return this.params[name];
    }
    transformFacetData(facetData) {
        return facetData.map((filter) => {
            switch (filter.type) {
                case 'tree':
                    const treeNodes = treeMap(filter.value, (node) => {
                        var _a;
                        const treeNode = {
                            label: node.label,
                            data: node.value,
                            children: node.children,
                        };
                        (_a = treeNode.children) === null || _a === void 0 ? void 0 : _a.forEach((child) => (child.parent = treeNode));
                        return treeNode;
                    });
                    return Object.assign(Object.assign({}, filter), { value: treeNodes });
                default:
                    return filter;
            }
        });
    }
    buildFacets() {
        const useDefaults = Object.values(this.params).every((v) => v === null && v === undefined);
        Object.entries(this.facetsConfig).forEach(([facetName]) => {
            var _a, _b, _c, _d;
            this.actions.addFacet.dispatch();
            const type = (_a = this.facetsConfig[facetName]) === null || _a === void 0 ? void 0 : _a.type;
            const value = useDefaults
                ? (_b = this.facetsConfig[facetName]) === null || _b === void 0 ? void 0 : _b.defaultValue : this.getParam(facetName);
            switch (type) {
                case 'radio':
                    this.selectedRadioItems[facetName] = value;
                    break;
                case 'singleSelect':
                    this.selectedListItems[facetName] = value && [value];
                    break;
                case 'multiSelect':
                    this.selectedListItems[facetName] = value !== null && value !== void 0 ? value : [];
                    break;
                case 'tree':
                    this.selectedTreeItems[facetName] = value !== null && value !== void 0 ? value : [];
                    this.buildTreeFacet(facetName);
                    break;
                case 'dateRange':
                case 'dateTimeRange':
                    const dateStart = `${facetName}Max`;
                    const dateEnd = `${facetName}Min`;
                    const maxDate = new Date(value === null || value === void 0 ? void 0 : value.max);
                    const minDate = new Date(value === null || value === void 0 ? void 0 : value.min);
                    this.selectedRangeItems[`${facetName}Max`] =
                        this.params[dateStart] !== undefined
                            ? this.parseDateFromString(this.params[dateStart])
                            : isNaN(Date.parse(value === null || value === void 0 ? void 0 : value.max))
                                ? null
                                : maxDate;
                    this.selectedRangeItems[`${facetName}Min`] =
                        this.params[dateEnd] !== undefined
                            ? this.parseDateFromString(this.params[dateEnd])
                            : isNaN(Date.parse(value === null || value === void 0 ? void 0 : value.min))
                                ? null
                                : minDate;
                    break;
                case 'numericRange':
                case 'currencyRange':
                    const totalAmountStart = `${facetName}Max`;
                    const totalAmountEnd = `${facetName}Min`;
                    this.selectedRangeItems[`${facetName}Max`] =
                        this.params[totalAmountStart] !== undefined
                            ? this.params[totalAmountStart]
                            : (_c = value === null || value === void 0 ? void 0 : value.max) !== null && _c !== void 0 ? _c : '';
                    this.selectedRangeItems[`${facetName}Min`] =
                        this.params[totalAmountEnd] !== undefined
                            ? this.params[totalAmountEnd]
                            : (_d = value === null || value === void 0 ? void 0 : value.min) !== null && _d !== void 0 ? _d : '';
                    break;
                default:
                    this.selectedTextItems[facetName] = value;
                    break;
            }
        });
        this.handleAdvancedSearch();
    }
    buildTreeFacet(facetName) {
        const filter = this.filters.find((it) => it.name === facetName);
        // Collect corresponding selected nodes
        const selectedItems = this.selectedTreeItems[filter.name];
        const selectedNodes = treeReduce(filter.value, [], (node, acc) => ((selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.includes(node.data)) ? [...acc, node] : acc));
        // Partially select all ancestors
        for (const node of selectedNodes) {
            if (!node.parent) {
                continue;
            }
            for (let ancestor = node.parent; !!ancestor; ancestor = ancestor.parent) {
                ancestor.partialSelected = true;
            }
        }
        this.selectedTreeNodes[filter.name] = selectedNodes;
    }
    handleAdvancedSearch() {
        this.filtersEvent(this.getAppliedFilters());
    }
    handleSingleSelectGroup($event, facetName) {
        // Pick the most recent option for a single select group
        const selectedOption = $event.pop();
        this.selectedListItems[facetName] = selectedOption && [selectedOption];
    }
    handleTreeSelect(facetName) {
        // Rebuild the selected tree items based on the selected nodes
        this.selectedTreeItems[facetName] = this.selectedTreeNodes[facetName].map((node) => node.data);
    }
    handleClear() {
        const radioItems = this.filters
            .filter((item) => item.type === 'radio')
            .reduce((items, config) => {
            return Object.assign(Object.assign({}, items), { [config.name]: config.value });
        }, {});
        Object.keys(this.selectedRadioItems).forEach((name) => {
            const value = radioItems[name][0];
            this.selectedRadioItems[name] = value.key;
            this.selectedYearChanged.emit(value.key);
        });
        this.selectedListItems = {};
        this.filters.forEach((facet) => {
            switch (facet.type) {
                case 'tree':
                    this.selectedTreeItems[facet.name] = [];
                    this.selectedTreeNodes[facet.name] = [];
                    break;
                case 'text':
                    this.selectedTextItems[facet.name] = '';
                    break;
                case 'dateRange':
                case 'dateTimeRange':
                case 'currencyRange':
                    this.selectedRangeItems[facet.name + 'Min'] = null;
                    this.selectedRangeItems[facet.name + 'Max'] = null;
                    break;
            }
        });
        this.selectedRadioItems['year'] !== undefined
            ? this.filtersEvent([`Year=${this.selectedRadioItems['year']}`])
            : this.filtersEvent([]);
        this.filtersParam = {};
        this.filterQueryParams.emit(this.filtersParam);
    }
    filtersEvent(filters) {
        this.setFilterEvent.emit({
            filters,
            options: { search: true },
        });
    }
    getAppliedFilters() {
        const appliedFilters = Object.keys(this.facetsConfig).reduce((filters, facetName) => {
            return Object.assign(Object.assign({}, filters), this.doForType(facetName));
        }, {});
        this.filterQueryParams.emit(this.filtersParam);
        return appliedFilters;
    }
    doForType(facetName) {
        const config = this.facetsConfig[facetName];
        switch (config.type) {
            case 'singleSelect':
            case 'multiSelect':
                const selectFilter = this.eq(facetName, this.selectedListItems[facetName]);
                this.filtersParam = Object.assign(this.filtersParam, Object.keys(selectFilter).length > 0 && {
                    [facetName]: this.selectedListItems[facetName],
                });
                return selectFilter;
            case 'tree':
                const treeFilter = this.eq(facetName, this.selectedTreeItems[facetName]);
                this.filtersParam = Object.assign(this.filtersParam, Object.keys(treeFilter).length > 0 && {
                    [facetName]: this.selectedTreeItems[facetName],
                });
                return treeFilter;
            case 'dateRange':
            case 'dateTimeRange':
                const dateFilter = this.dateRange(facetName, this.selectedRangeItems[facetName + 'Min'], this.selectedRangeItems[facetName + 'Max']);
                this.filtersParam = Object.assign(this.filtersParam, dateFilter[facetName] &&
                    Object.keys(dateFilter[facetName]).reduce((filter, key) => {
                        const newKey = key === 'gte' ? `${facetName}Min` : `${facetName}Max`;
                        return Object.assign(Object.assign({}, filter), { [newKey]: this.selectedRangeItems[newKey]
                                .toISOString()
                                .split('T')[0] });
                    }, {}));
                return dateFilter;
            case 'numericRange':
            case 'currencyRange':
                const rangeFilter = this.range(facetName, this.selectedRangeItems[facetName + 'Min'], this.selectedRangeItems[facetName + 'Max']);
                this.filtersParam = Object.assign(this.filtersParam, rangeFilter[facetName] &&
                    Object.keys(rangeFilter[facetName]).reduce((filter, key) => {
                        const newKey = key === 'gte' ? `${facetName}Min` : `${facetName}Max`;
                        return Object.assign(Object.assign({}, filter), { [newKey]: this.selectedRangeItems[newKey] });
                    }, {}));
                return rangeFilter;
            case 'radio':
                const radioFilter = this.eq(facetName, this.selectedRadioItems[facetName]);
                this.filtersParam = Object.assign(this.filtersParam, Object.keys(radioFilter).length > 0 && {
                    [facetName]: this.selectedRadioItems[facetName],
                });
                return radioFilter;
            default:
                const defaultFilter = this.fz(facetName, this.selectedTextItems[facetName]);
                this.filtersParam = Object.assign(this.filtersParam, Object.keys(defaultFilter).length > 0 && {
                    [facetName]: this.selectedTextItems[facetName],
                });
                return defaultFilter;
        }
    }
    fz(facetName, value = '') {
        return value === '' ? {} : { [facetName]: { fz: [value] } };
    }
    dateRange(facetName, min, max) {
        const body = Object.assign({}, min && { gte: [min.toISOString()] }, max && { lt: [add$1(max, { days: 1 }).toISOString()] });
        return min || max
            ? {
                [facetName]: body,
            }
            : {};
    }
    range(facetName, min, max) {
        const hasMin = typeof min === 'number';
        const hasMax = typeof max === 'number';
        const body = Object.assign({}, hasMin && { gte: [`${min}`] }, hasMax && { lte: [`${max}`] });
        return hasMin || hasMax
            ? {
                [facetName]: body,
            }
            : {};
    }
    eq(facetName, values = []) {
        return values.length === 0 ? {} : { [facetName]: { eq: values } };
    }
    getAdvancedFiltersByYear(year) {
        Object.keys(this.selectedListItems).forEach((name) => (this.selectedListItems[name] = []));
        this.selectedYearChanged.emit(year);
    }
    parseDateFromString(dateString) {
        const [year, month, day] = `${dateString}`.split('-').map(Number);
        return new Date(year, month - 1, day);
    }
    ngOnDestroy() {
        if (this.subscriptions) {
            this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        }
    }
};
AdvancedSearchLoaderComponent.ctorParameters = () => [
    { type: ActionUiService }
];
__decorate([
    Input()
], AdvancedSearchLoaderComponent.prototype, "facetsConfig", void 0);
__decorate([
    Input('facetData')
], AdvancedSearchLoaderComponent.prototype, "facetDataSetter", null);
__decorate([
    Input('params')
], AdvancedSearchLoaderComponent.prototype, "paramsSetter", null);
__decorate([
    Input()
], AdvancedSearchLoaderComponent.prototype, "dateFormat", void 0);
__decorate([
    Input()
], AdvancedSearchLoaderComponent.prototype, "languageCode", void 0);
__decorate([
    Output()
], AdvancedSearchLoaderComponent.prototype, "setFilterEvent", void 0);
__decorate([
    Output()
], AdvancedSearchLoaderComponent.prototype, "selectedYearChanged", void 0);
__decorate([
    Output()
], AdvancedSearchLoaderComponent.prototype, "filterQueryParams", void 0);
AdvancedSearchLoaderComponent = __decorate([
    Component({
        selector: 'lib-advanced-search',
        template: "<div class=\"vertical-flow advanced-search\">\r\n  <div class=\"horizontal-fixed space-between-075\">\r\n    <button\r\n      pButton\r\n      class=\"ds width-50\"\r\n      type=\"button\"\r\n      label=\"{{ 'ADVANCED_SEARCH.CLEAR' | transloco }}\"\r\n      (click)=\"handleClear()\"\r\n      data-testid=\"searchContainerFiltersClearButton\"\r\n    ></button>\r\n    <button\r\n      pButton\r\n      class=\"ds width-50\"\r\n      type=\"button\"\r\n      label=\"{{ 'ADVANCED_SEARCH.APPLY' | transloco }}\"\r\n      (click)=\"handleAdvancedSearch()\"\r\n      data-testid=\"searchContainerFiltersApplyButton\"\r\n    ></button>\r\n  </div>\r\n  <div class=\"space-before-100\">\r\n    <p-accordion\r\n      styleClass=\"advancedSearchTabHeader\"\r\n      expandIcon=\"pi pi-caret-right\"\r\n      collapseIcon=\"pi pi-caret-down\"\r\n      (onOpen)=\"actions.toggleFacet.dispatch($event.index)\"\r\n      (onClose)=\"actions.toggleFacet.dispatch($event.index)\"\r\n      [multiple]=\"true\"\r\n    >\r\n      <div class=\"vertical-flow space-between-025\">\r\n        <ng-container *ngFor=\"let filter of filters; let index = index\">\r\n          <p-accordionTab\r\n            [selected]=\"state.facets[index]\"\r\n            *ngIf=\"filter.type === 'singleSelect'\"\r\n            class=\"ui-selection-helper\"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n                </div>\r\n                <div\r\n                  class=\"facet-icon\"\r\n                  *ngIf=\"selectedListItems[filter.name]?.length\"\r\n                >\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div\r\n              *ngFor=\"let x of filter.value; let i = index\"\r\n              class=\"vertical-flow\"\r\n            >\r\n              <p-checkbox\r\n                [ngClass]=\"i > 0 ? 'space-before-075' : ' '\"\r\n                [label]=\"x.label\"\r\n                [name]=\"filter.name\"\r\n                [value]=\"x.value\"\r\n                [(ngModel)]=\"selectedListItems[filter.name]\"\r\n                (ngModelChange)=\"handleSingleSelectGroup($event, filter.name)\"\r\n              >\r\n              </p-checkbox>\r\n            </div>\r\n          </p-accordionTab>\r\n          <p-accordionTab\r\n            [selected]=\"state.facets[index]\"\r\n            *ngIf=\"filter.type === 'multiSelect'\"\r\n            class=\"ui-selection-helper\"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n                </div>\r\n                <div\r\n                  class=\"facet-icon\"\r\n                  *ngIf=\"selectedListItems[filter.name]?.length\"\r\n                >\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div\r\n              *ngFor=\"let x of filter.value; let i = index\"\r\n              class=\"vertical-flow\"\r\n            >\r\n              <p-checkbox\r\n                [ngClass]=\"i > 0 ? 'space-before-075' : ' '\"\r\n                [label]=\"x.label\"\r\n                [name]=\"filter.name\"\r\n                [value]=\"x.value\"\r\n                [(ngModel)]=\"selectedListItems[filter.name]\"\r\n              >\r\n              </p-checkbox>\r\n            </div>\r\n          </p-accordionTab>\r\n          <p-accordionTab\r\n            [selected]=\"state.facets[index]\"\r\n            *ngIf=\"filter.type === 'tree'\"\r\n            class=\"ui-selection-helper\"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n                </div>\r\n                <div\r\n                  class=\"facet-icon\"\r\n                  *ngIf=\"selectedTreeItems[filter.name]?.length\"\r\n                >\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div class=\"vertical-flow\">\r\n              <p-tree\r\n                [value]=\"filter.value\"\r\n                selectionMode=\"checkbox\"\r\n                [(selection)]=\"selectedTreeNodes[filter.name]\"\r\n                (onNodeSelect)=\"handleTreeSelect(filter.name)\"\r\n                (onNodeUnselect)=\"handleTreeSelect(filter.name)\"\r\n              >\r\n              </p-tree>\r\n            </div>\r\n          </p-accordionTab>\r\n          <p-accordionTab\r\n            *ngIf=\"filter.type === 'radio'\"\r\n            (onOpen)=\"actions.toggleFacet.dispatch($event.index)\"\r\n            (onClose)=\"actions.toggleFacet.dispatch($event.index)\"\r\n            [selected]=\"state.facets[index]\"\r\n            class=\"ui-selection-helper\"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n\r\n                  <span\r\n                    *ngIf=\"\r\n                      selectedRadioItems[filter.name] &&\r\n                      selectedRadioItems[filter.name] != null &&\r\n                      selectedRadioItems[filter.name] != '' &&\r\n                      selectedRadioItems[filter.name] == 'Last90'\r\n                    \"\r\n                    class=\"yearDisplayRight\"\r\n                  >\r\n                    - {{ 'ADVANCED_SEARCH.Last90' | transloco }}\r\n                  </span>\r\n                  <span\r\n                    *ngIf=\"\r\n                      selectedRadioItems[filter.name] &&\r\n                      selectedRadioItems[filter.name] != null &&\r\n                      selectedRadioItems[filter.name] != '' &&\r\n                      selectedRadioItems[filter.name] != 'Last90'\r\n                    \"\r\n                    class=\"yearDisplayRight\"\r\n                  >\r\n                    - {{ selectedRadioItems[filter.name] }}\r\n                  </span>\r\n                </div>\r\n                <div\r\n                  class=\"facet-icon\"\r\n                  *ngIf=\"\r\n                    selectedRadioItems[filter.name] &&\r\n                    selectedRadioItems[filter.name] != null\r\n                  \"\r\n                >\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div\r\n              *ngFor=\"let x of filter[filter.type].value; let i = index\"\r\n              class=\"vertical-flow advanced-filter-radio-control\"\r\n            >\r\n              <div\r\n                class=\"horizontal-flow space-between-100\"\r\n                [ngClass]=\"i > 0 ? 'space-before-075' : ' '\"\r\n              >\r\n                <p-radioButton\r\n                  [inputId]=\"x.key\"\r\n                  name=\"x\"\r\n                  [value]=\"x.key\"\r\n                  [(ngModel)]=\"selectedRadioItems[filter.name]\"\r\n                  (onClick)=\"getAdvancedFiltersByYear(x.key)\"\r\n                ></p-radioButton>\r\n                <label [for]=\"x.key\" class=\"advanced-filter-radio-label\">{{\r\n                  x.name\r\n                }}</label>\r\n              </div>\r\n            </div>\r\n          </p-accordionTab>\r\n          <p-accordionTab\r\n            [selected]=\"state.facets[index]\"\r\n            *ngIf=\"filter.type === 'text'\"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n                </div>\r\n                <div class=\"facet-icon\" *ngIf=\"selectedTextItems[filter.name]\">\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div class=\"vertical-flow\">\r\n              <input\r\n                pInputText\r\n                type=\"text\"\r\n                class=\"ui-inputtext space-before-025\"\r\n                [(ngModel)]=\"selectedTextItems[filter.name]\"\r\n              />\r\n            </div>\r\n          </p-accordionTab>\r\n          <p-accordionTab\r\n            [selected]=\"state.facets[index]\"\r\n            *ngIf=\"\r\n              filter.type === 'dateRange' || filter.type === 'dateTimeRange'\r\n            \"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n                </div>\r\n                <div\r\n                  class=\"facet-icon\"\r\n                  *ngIf=\"\r\n                    selectedRangeItems[filter.name + 'Min'] ||\r\n                    selectedRangeItems[filter.name + 'Max']\r\n                  \"\r\n                >\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div class=\"vertical-flow space-between-075 input-filter\">\r\n              <div>{{ 'ADVANCED_SEARCH.RANGE' | transloco }}</div>\r\n              <div>\r\n                <p-calendar\r\n                  [showIcon]=\"true\"\r\n                  [dateFormat]=\"dateFormat.primeNgFormat\"\r\n                  [placeholder]=\"dateFormat.description\"\r\n                  [(ngModel)]=\"selectedRangeItems[filter.name + 'Min']\"\r\n                  (onSelect)=\"getValidDate($event, filter.name)\"\r\n                  [locale]=\"locale\"\r\n                  appendTo=\"body\"\r\n                ></p-calendar>\r\n              </div>\r\n              <div>\r\n                <p-calendar\r\n                  [dateFormat]=\"dateFormat.primeNgFormat\"\r\n                  [placeholder]=\"dateFormat.description\"\r\n                  [(ngModel)]=\"selectedRangeItems[filter.name + 'Max']\"\r\n                  [showIcon]=\"true\"\r\n                  appendTo=\"body\"\r\n                  [locale]=\"locale\"\r\n                  (onSelect)=\"getValidDate($event, filter.name)\"\r\n                ></p-calendar>\r\n              </div>\r\n            </div>\r\n          </p-accordionTab>\r\n          <p-accordionTab\r\n            [selected]=\"state.facets[index]\"\r\n            *ngIf=\"\r\n              filter.type === 'numberRange' || filter.type === 'numericRange'\r\n            \"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n                </div>\r\n                <div\r\n                  class=\"facet-icon\"\r\n                  *ngIf=\"this[filter.name + 'Min'] || this[filter.name + 'Max']\"\r\n                >\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div class=\"vertical-flow space-between-075 input-filter\">\r\n              <div>{{ 'ADVANCED_SEARCH.MINMAX' | transloco }}</div>\r\n              <div>\r\n                <p-inputNumber\r\n                  mode=\"decimal\"\r\n                  placeholder=\"$0\"\r\n                  [(ngModel)]=\"this[filter.name + 'Min']\"\r\n                ></p-inputNumber>\r\n              </div>\r\n              <div>\r\n                <p-inputNumber\r\n                  mode=\"decimal\"\r\n                  placeholder=\"$1000000\"\r\n                  [(ngModel)]=\"this[filter.name + 'Max']\"\r\n                ></p-inputNumber>\r\n              </div>\r\n            </div>\r\n          </p-accordionTab>\r\n          <p-accordionTab\r\n            [selected]=\"state.facets[index]\"\r\n            *ngIf=\"filter.type === 'currencyRange'\"\r\n          >\r\n            <p-header>\r\n              <div class=\"facet-header\">\r\n                <div class=\"facet-title\">\r\n                  {{ filter.label }}\r\n                </div>\r\n                <div\r\n                  class=\"facet-icon\"\r\n                  *ngIf=\"\r\n                    selectedRangeItems[filter.name + 'Min'] ||\r\n                    selectedRangeItems[filter.name + 'Max']\r\n                  \"\r\n                >\r\n                  <img [src]=\"appliedIcon\" />\r\n                </div>\r\n              </div>\r\n            </p-header>\r\n            <div class=\"vertical-flow space-between-075 input-filter\">\r\n              <div>{{ 'ADVANCED_SEARCH.MINMAX' | transloco }}</div>\r\n              <div>\r\n                <p-inputNumber\r\n                  mode=\"decimal\"\r\n                  placeholder=\"$0\"\r\n                  [minFractionDigits]=\"2\"\r\n                  [maxFractionDigits]=\"2\"\r\n                  [(ngModel)]=\"selectedRangeItems[filter.name + 'Min']\"\r\n                ></p-inputNumber>\r\n              </div>\r\n              <div>\r\n                <p-inputNumber\r\n                  mode=\"decimal\"\r\n                  placeholder=\"$1000000\"\r\n                  [minFractionDigits]=\"2\"\r\n                  [maxFractionDigits]=\"2\"\r\n                  [(ngModel)]=\"selectedRangeItems[filter.name + 'Max']\"\r\n                ></p-inputNumber>\r\n              </div>\r\n            </div>\r\n          </p-accordionTab>\r\n        </ng-container>\r\n      </div>\r\n    </p-accordion>\r\n  </div>\r\n</div>\r\n",
        providers: [ActionUiService]
    })
], AdvancedSearchLoaderComponent);
function treeMap(roots, map) {
    return roots.map((root) => {
        const children = treeMap(root.children, map);
        return map(Object.assign(Object.assign({}, root), { children }));
    });
}
function treeReduce(roots, initial, accumulate) {
    if (roots.length === 0) {
        return initial;
    }
    let reduced = initial;
    roots.forEach((node) => {
        reduced = accumulate(node, reduced);
        reduced = treeReduce(node.children, reduced, accumulate);
    });
    return reduced;
}

let AdvancedSearchLoaderModule = class AdvancedSearchLoaderModule {
};
AdvancedSearchLoaderModule = __decorate([
    NgModule({
        declarations: [AdvancedSearchLoaderComponent],
        imports: [
            CommonModule,
            AccordionModule,
            ButtonModule,
            CheckboxModule$1,
            FormsModule,
            MultiSelectModule$1,
            RadioButtonModule,
            TreeModule,
            ReactiveFormsModule,
            TranslocoModule,
            CalendarModule$1,
            InputNumberModule,
        ],
        exports: [AdvancedSearchLoaderComponent],
    })
], AdvancedSearchLoaderModule);

let SearchContainerLoaderModule = class SearchContainerLoaderModule {
};
SearchContainerLoaderModule = __decorate([
    NgModule({
        declarations: [SearchContainerLoaderComponent],
        imports: [
            CommonModule,
            ButtonModule,
            CheckboxModule$1,
            ConfirmDialogModule$1,
            DropdownModule,
            FormsModule,
            MultiSelectModule$1,
            RadioButtonModule,
            ReactiveFormsModule,
            RouterModule,
            SearchContainerModule,
            TabViewModule,
            ToolbarModule,
            TranslocoModule,
            TreeTableModule,
            ProgressSpinnerModule,
            UtilsModule,
            DialogModule$1,
            TableModule$1,
            CalendarModule$1,
            InputNumberModule,
            AdvancedSearchLoaderModule,
        ],
        providers: [ConfirmationService$1],
        exports: [SearchContainerLoaderComponent, AdvancedSearchLoaderComponent],
    })
], SearchContainerLoaderModule);

/*
 * Public API Surface of search-container
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AdvancedSearchLoaderComponent, AdvancedSearchLoaderModule, BaseHttpService, SearchContainerComponent, SearchContainerLoaderComponent, SearchContainerLoaderModule, SearchContainerLoaderService, SearchContainerModule, SearchContainerService, LocalStorageService as ɵa, SearchDirective as ɵb, PhoenixGridContainerComponent as ɵc, GridService as ɵd, PhoenixGridComponent as ɵe, LayoutComponent as ɵf, ColumnComponent as ɵg };
//# sourceMappingURL=lbmx-search-container.js.map
